/**
 * Pill — small uppercase status badge. Unified across customer pages so
 * status semantics read the same everywhere: emerald = good, amber = needs
 * attention, rose = bad, slate = neutral/closed, teal/sky/violet = info.
 */
import React from 'react';

export type PillTone = 'emerald' | 'rose' | 'amber' | 'slate' | 'teal' | 'sky' | 'violet';

interface Props {
  label: string;
  tone?: PillTone;
  size?: 'xs' | 'sm';
}

const toneMap: Record<PillTone, string> = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rose:    'bg-rose-50 text-rose-700 border-rose-200',
  amber:   'bg-amber-50 text-amber-800 border-amber-200',
  slate:   'bg-slate-50 text-slate-700 border-slate-200',
  teal:    'bg-teal-50 text-teal-700 border-teal-200',
  sky:     'bg-sky-50 text-sky-700 border-sky-200',
  violet:  'bg-violet-50 text-violet-700 border-violet-200',
};

const Pill: React.FC<Props> = ({ label, tone = 'slate', size = 'xs' }) => {
  const sizing = size === 'sm' ? 'text-xs px-2.5 py-1' : 'text-[10px] uppercase tracking-wider px-2 py-0.5';
  return <span className={`inline-flex items-center rounded-md border ${sizing} ${toneMap[tone]}`}>{label}</span>;
};

/**
 * Map a freeform status string from our APIs to a unified Pill tone +
 * label. Centralizes the colour decisions so every page agrees.
 */
export function pillForStatus(status: string): { label: string; tone: PillTone } {
  const s = (status || '').toLowerCase();
  if (['paid', 'completed', 'active', 'resolved', 'verified', 'running', 'success', 'successful'].includes(s)) {
    return { label: status, tone: 'emerald' };
  }
  if (['open', 'in_progress', 'pending', 'sent', 'verifying', 'processing'].includes(s)) {
    return { label: status, tone: 'amber' };
  }
  if (['failed', 'overdue', 'rejected', 'suspended', 'error'].includes(s)) {
    return { label: status, tone: 'rose' };
  }
  if (['cancelled', 'closed', 'terminated', 'stopped', 'draft'].includes(s)) {
    return { label: status, tone: 'slate' };
  }
  return { label: status || '—', tone: 'slate' };
}

export default Pill;
